import { useEffect } from 'react';
import { Box, Spinner, Text, VStack } from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { setAuthenticated } from '../redux/features/authSlice';
import { attributeIfPending } from '../referrals/referralApi';

const mainServiceUrl = import.meta.env.VITE_MAIN_SERVICE_URL || '';

export default function AuthCallbackPage() {
  const dispatch = useDispatch();
  const searchParams = new URLSearchParams(window.location.search);
  // main-service signals OAuth errors in TWO shapes depending on the
  // failure kind. Recognise both — previously we only checked `?error=`,
  // which silently swallowed real problems like NOT_FOUND on a Google
  // login for a user that doesn't exist yet ("please sign up first").
  //   1. Native/social provider failure   → `?error=<code>`
  //   2. Cognito social callback failure  → `?success=false&errorCode=<code>&message=<text>`
  const errorLegacy = searchParams.get('error');
  const successFalse = searchParams.get('success') === 'false';
  const errorCode = errorLegacy || (successFalse ? searchParams.get('errorCode') : null);
  const errorMessage = searchParams.get('message');
  const isError = !!errorCode;

  useEffect(() => {
    const verify = async () => {
      if (!isError) {
        try {
          const res = await fetch(`${mainServiceUrl}/auth/verify`, { credentials: 'include' });
          if (res.ok) {
            const data = await res.json();
            if (data?.authenticated) {
              dispatch(setAuthenticated({ user: data.user || {} }));
              // Fire-and-forget referral attribution — same as native
              // signup path. Safe to call on every successful auth: the
              // helper no-ops when no code was stashed, and the BE dedup
              // guarantees a specific user is only ever attributed once
              // (unique index on {app, refereeUserId}). So repeat logins
              // by an existing user won't re-fire the grant.
              const uid = data?.user?.id || data?.user?._id;
              if (uid) void attributeIfPending(String(uid));
            }
          }
        } catch {}
      }
      setTimeout(() => {
        if (isError) {
          const params = new URLSearchParams({ error: errorCode || 'AUTH_FAILED' });
          if (errorMessage) params.set('reason', errorMessage);
          window.location.href = `/login?${params.toString()}`;
          return;
        }
        // Widget hand-off rescue: if the anonymous visitor arrived here
        // via a widget generate → login → OAuth round-trip, App.tsx
        // stashed the pending id in localStorage as soon as it saw
        // `?pending=` in the URL. Cognito's OAuth flow drops query state
        // that we tried to carry, so this stash is the only reliable
        // hand-off. Redirecting to /composer?pending=<id> — the composer
        // is the new default UI (cutover 2026-09-30); its hydration path
        // reads the same ?pending= param the legacy generator used.
        let target = '/composer';
        try {
          const pendingId = localStorage.getItem('mg.pending.id');
          const exp = Number(localStorage.getItem('mg.pending.exp') || '0');
          if (pendingId && /^[a-f0-9]{24}$/.test(pendingId) && exp > Date.now()) {
            target = `/composer?pending=${encodeURIComponent(pendingId)}`;
          } else if (pendingId) {
            // Stale — clean up before falling through to the default.
            localStorage.removeItem('mg.pending.id');
            localStorage.removeItem('mg.pending.exp');
          }
        } catch { /* ignore */ }
        window.location.href = target;
      }, 1000);
    };
    verify();
  }, [isError, errorCode, errorMessage, dispatch]);

  return (
    <VStack minH="100vh" justify="center" spacing={4}>
      <Spinner size="xl" color="brand.500" />
      <Text color="gray.600">{isError ? 'Authentication failed. Redirecting...' : 'Signing you in...'}</Text>
      {isError && (
        <Text fontSize="sm" color="red.500" maxW="400px" textAlign="center">
          {errorMessage || errorCode}
        </Text>
      )}
    </VStack>
  );
}
